import Logger from '../logging/logger';
import requestIdleCallback from '../shims/requestIdleCallback';
import { yieldToMainThread } from '../yield';
import { closeExpandedDropdowns } from '../dropdown';
import { isTouchDevice } from '../user/device-info';
import { onPressEscape } from '../events/keydown';

type Option = {
    targetID: string;
    targetElement: HTMLElement;
    options: Array<Option>;
}

export default class Sidenav {
    _root: HTMLElement;
    _toggle: HTMLButtonElement;
    _options: Array<Option>;
    _openDrawerCount: number = 1;

    _drawers: Array<HTMLElement>;

    _overlay: HTMLElement;
    _overlayInitialTouch: Touch | null = null;

    _drawerContainer: HTMLElement;
    _drawerContainerInitialTouch: Touch | null = null;

    _links: NodeListOf<HTMLAnchorElement>;

    constructor( root: HTMLElement ) {
        this._root = root;

        this._init();

        Logger.info( 'Initialsed sidenav', {
            options: this._options,
        } );
    }

    _init(): void {
        this._toggle = document.querySelector<HTMLButtonElement>( '#burger-menu' ) as HTMLButtonElement;
        this._drawers = Array.from( this._root.querySelectorAll<HTMLElement>( '.sidenav__drawer' ) );
        this._overlay = this._root.querySelector<HTMLElement>( '.sidenav__overlay' ) as HTMLElement;
        this._drawerContainer = this._root.querySelector<HTMLElement>( '.sidebar__drawers' ) as HTMLElement;
        this._links = this._root.querySelectorAll<HTMLAnchorElement>( 'a' );

        const buildOption = ( optionElement: HTMLButtonElement ): Option => {
            const targetID = optionElement.dataset.menuId as string;

            const next = this._root.querySelector<HTMLElement>( `.sidenav__drawer[data-id="${targetID}"]` );

            if ( !next ) {
                throw new Error( `sidenav drawer: ${targetID} not found` );
            }

            const option: Option = {
                targetID: targetID,
                targetElement: next,
                options: [],
            };

            const nextOptions: Array<HTMLButtonElement> = Array.from( next.querySelectorAll<HTMLButtonElement>( '.sidenav__option[data-menu-id]' ) );

            if ( nextOptions.length > 0 ) {
                option.options = nextOptions.map( buildOption );
            }

            optionElement.addEventListener( 'click', ( event: Event ) => {
                this._onDrawerOptionClick( option );
            } );

            const btnBack = next.querySelector<HTMLButtonElement>( '.sidenav__btnback' );

            if ( btnBack ) {
                btnBack.addEventListener( 'click', ( event: Event ): void => {
                    this._onDrawerBackButtonClick( next );
                } );
            }

            return option;
        };

        const initialOptions: Array<HTMLButtonElement> = Array.from( this._defaultDrawer.querySelectorAll<HTMLButtonElement>( '.sidenav__option[data-menu-id]' ) );

        this._options = initialOptions.map( ( option: HTMLButtonElement ): Option => buildOption( option ) );

        this._toggle.addEventListener( 'click', this._onToggleClick.bind( this ) );

        document.querySelectorAll<HTMLButtonElement>( 'button[data-open-sidenav]' ).forEach( ( btn: HTMLButtonElement ) => {
            const option: Option | undefined = this._getOptionByID( btn.dataset.openSidenav as string );

            if ( option ) {
                btn.addEventListener( 'click', async ( event: Event ) => {
                    await this.open();
                    this._openDrawer( option.targetElement, this._openDrawerCount );
                } );
            }
        } );

        this._root.addEventListener( 'click', ( event: Event ) => {
            if ( event.target && event.target instanceof HTMLElement && event.target.matches( '.sidenav__overlay' ) ) {
                this.close();
            }
        } );

        window.addEventListener( 'pageshow', ( event: PageTransitionEvent ) => {
            if ( event.persisted ) {
                this.close();
            }
        } );

        if ( isTouchDevice() ) {
            this._overlay.addEventListener( 'touchstart', this._onOverlayTouchStart.bind( this ) );
            this._overlay.addEventListener( 'touchmove', this._onOverlayTouchMove.bind( this ) );
            this._overlay.addEventListener( 'touchend', this._onOverlayTouchStart.bind( this ) );

            this._drawerContainer.addEventListener( 'touchstart', this._onDrawerContainerTouchStart.bind( this ) );
            this._drawerContainer.addEventListener( 'touchmove', this._onDrawerContainerTouchMove.bind( this ) );
            this._drawerContainer.addEventListener( 'touchend', this._onDrawerContainerTouchEnd.bind( this ) );
        }

        onPressEscape( this.close.bind( this ) );

        this._links.forEach( ( link: HTMLAnchorElement ) => {
            link.addEventListener( 'click', async () => {
                if ( this._isOpen ) {
                    await this.close();
                }
            } );
        } );
    }

    async open(): Promise<void> {
        window.megaMenu.hide();

        if ( this._isOpen ) {
            return;
        }

        await yieldToMainThread();

        this._toggle.classList.add( Sidenav._class.burgerActive );

        document.body.classList.add( Sidenav._class.bodyOpen );

        document.documentElement.style.overflow = 'hidden';

        await yieldToMainThread();

        requestIdleCallback( () => {
            document.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );
            closeExpandedDropdowns();
        } );
    }

    async close(): Promise<void> {
        if ( !this._isOpen ) {
            return;
        }

        this._toggle.classList.remove( Sidenav._class.burgerActive );

        this._root.classList.add( Sidenav._class.sidenavHide );

        window.setTimeout( async () => {
            this._root.classList.remove( Sidenav._class.sidenavHide );
            document.body.classList.remove( Sidenav._class.bodyOpen );
            await yieldToMainThread();
        }, 250 );

        requestIdleCallback( () => {
            this._defaultDrawer.scrollTop = 0;
            document.documentElement.style.overflow = '';
        } );

        document.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );

        if ( this._openDrawerCount > 1 ) {
            this._openDrawerCount = 1;

            this._root.querySelectorAll<HTMLElement>( '.sidenav__drawer' ).forEach( ( drawer: HTMLElement ) => {
                if ( drawer.dataset.id === 'default' ) {
                    return;
                }

                this._closeDrawer( drawer );
            } );
        }
    }

    _onDrawerOptionClick( option: Option ): void {
        this._openDrawer( option.targetElement, this._openDrawerCount );
    }

    _onDrawerBackButtonClick( drawer: HTMLElement ): void {
        if ( !drawer.classList.contains( Sidenav._class.drawerVisibility ) ) {
            return;
        }

        this._closeDrawer( drawer );
    }

    _openDrawer( drawer: HTMLElement, zIndex: number ): void {
        this._openDrawerCount = this._openDrawerCount + 1;

        drawer.dataset.index = this._openDrawerCount.toString();

        drawer.style.zIndex = this._openDrawerCount.toString();

        drawer.classList.add( Sidenav._class.drawerVisibility );
    }

    _closeDrawer( drawer: HTMLElement ): void {
        if ( !drawer || !drawer.classList.contains( Sidenav._class.drawerVisibility ) ) {
            return;
        }

        if ( this._openDrawerCount > 1 ) {
            this._openDrawerCount = this._openDrawerCount - 1;
        }

        drawer.classList.remove( Sidenav._class.drawerVisibility );

        window.setTimeout( () => {
            drawer.removeAttribute( 'data-index' );
            drawer.style.zIndex = '';
            drawer.scrollTop = 0;
        }, 250 );
    }

    _onToggleClick( event: Event ): void {
        if ( this._isOpen ) {
            this.close();
        } else {
            this.open();
        }
    }

    _onOverlayTouchStart( event: TouchEvent ): void {
        if ( !event.touches ) {
            return;
        }

        this._overlayInitialTouch = event.changedTouches[ 0 ];
    }

    _onOverlayTouchEnd( event: TouchEvent ): void {
        this._overlayInitialTouch = null;
    }

    async _onOverlayTouchMove( event: TouchEvent ): Promise<void> {
        if ( !this._overlayInitialTouch || !event.changedTouches ) {
            return;
        }

        const startX = this._overlayInitialTouch.screenX;
        const endX = event.changedTouches[ 0 ].screenX;

        const minMoveDistance: number = 150; // minimum swipe distance in px to trigger a close

        if ( endX > startX && ( endX - startX ) >= minMoveDistance ) {
            this._overlayInitialTouch = null;
            await this.close();
        }
    }

    _onDrawerContainerTouchStart( event: TouchEvent ): void {
        if ( !event.touches ) {
            return;
        }

        this._drawerContainerInitialTouch = event.changedTouches[ 0 ];
    }

    _onDrawerContainerTouchEnd( event: TouchEvent ): void {
        this._drawerContainerInitialTouch = null;
    }

    _onDrawerContainerTouchMove( event: TouchEvent ): void {
        if ( !this._drawerContainerInitialTouch || !event.changedTouches ) {
            return;
        }

        const startX = this._drawerContainerInitialTouch.screenX;
        const endX = event.changedTouches[ 0 ].screenX;

        const minMoveDistance: number = 100; // minimum swipe distance in px to trigger a drawer close

        if ( endX > startX && ( endX - startX ) >= minMoveDistance ) {
            const drawer = this._drawers.find( ( d: HTMLElement ) => d.dataset.index === this._openDrawerCount.toString() ) as HTMLElement;

            if ( !drawer ) {
                return;
            }

            this._closeDrawer( drawer );

            this._drawerContainerInitialTouch = null;
        }
    }

    get _isOpen(): boolean {
        return document.body.classList.contains( Sidenav._class.bodyOpen );
    }

    get _defaultDrawer(): HTMLElement {
        return this._drawers.find( ( d: HTMLElement ) => d.dataset.id === 'default' ) as HTMLElement;
    }

    _getOptionByID( id: string ): Option | undefined {
        const findOption = ( option: Option ): Option | undefined => {
            if ( option.targetID === id ) {
                return option;
            }
            return option.options.find( findOption );
        };

        return this._options.find( findOption );
    }

    static _class: Record<string, string> = {
        bodyOpen: 'sidenav-open',
        drawerVisibility: 'sidenav__drawer--visible',
        burgerActive: 'sidenav__toggle--active',
        sidenavHide: 'sidenav--hide',
    };
}

export function closeSidenav(): void {
    if ( window.sidenav ) {
        window.sidenav.close();
    }
}
